function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import deepEqual from 'fast-deep-equal';
import { Formatter, TableUtils } from '@deephaven/jsapi-utils';
import Log from '@deephaven/log';
import { EventShimCustomEvent, PromiseUtils } from '@deephaven/utils';
import IrisGridTableModel from "./IrisGridTableModel.js";
import IrisGridPartitionedTableModel from "./IrisGridPartitionedTableModel.js";
import IrisGridTreeTableModel from "./IrisGridTreeTableModel.js";
import IrisGridModel from "./IrisGridModel.js";
import { isIrisGridTableModelTemplate } from "./IrisGridTableModelTemplate.js";
import { isPartitionedGridModelProvider, isPartitionedGridModel } from "./PartitionedGridModel.js";
var log = Log.module('IrisGridProxyModel');
function makeModel(dh, table, formatter, inputTable) {
  if (TableUtils.isTreeTable(table)) {
    return new IrisGridTreeTableModel(dh, table, formatter);
  }
  if (TableUtils.isPartitionedTable(table)) {
    return new IrisGridPartitionedTableModel(dh, table, formatter);
  }
  return new IrisGridTableModel(dh, table, formatter, inputTable);
}

/**
 * Model which proxies calls to other IrisGridModels.
 * This allows for operations that generate new tables, like rollups.
 * The proxy model will call any methods it has implemented and delegate any
 * it does not implement to the underlying model.
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
class IrisGridProxyModel extends IrisGridModel {
  constructor(dh, table) {
    var formatter = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new Formatter(dh);
    var inputTable = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    super(dh);

    // The EventTarget methods must be bound to this instance
    // Otherwise they throw errors because of the Proxy
    /**
     * @param dh JSAPI instance
     * @param table Iris data table to be used in the model
     * @param formatter The formatter to use when getting formats
     * @param inputTable Iris input table associated with this table
     */
    _defineProperty(this, "model", void 0);
    _defineProperty(this, "dh", void 0);
    _defineProperty(this, "originalModel", void 0);
    _defineProperty(this, "modelPromise", void 0);
    _defineProperty(this, "rollup", void 0);
    _defineProperty(this, "partition", void 0);
    _defineProperty(this, "selectDistinct", void 0);
    _defineProperty(this, "currentViewport", void 0);
    _defineProperty(this, "setViewport", (top, bottom, columns) => {
      this.currentViewport = {
        top,
        bottom,
        columns
      };
      this.model.setViewport(top, bottom, columns);
    });
    this.addEventListener = this.addEventListener.bind(this);
    this.removeEventListener = this.removeEventListener.bind(this);
    this.dispatchEvent = this.dispatchEvent.bind(this);
    this.handleModelEvent = this.handleModelEvent.bind(this);
    var model = makeModel(dh, table, formatter, inputTable);
    this.dh = dh;
    this.originalModel = model;
    this.model = model;
    this.modelPromise = null;
    this.rollup = null;
    this.partition = null;
    this.selectDistinct = [];

    // eslint-disable-next-line no-constructor-return
    return new Proxy(this, {
      // We want to use any properties on the proxy model if defined
      // If not, then proxy to the underlying model
      get(target, prop, receiver) {
        var _Object$getOwnPropert;
        // Does this class have a getter for the prop
        // Getter functions are on the prototype
        var proxyHasGetter = ((_Object$getOwnPropert = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(target), prop)) === null || _Object$getOwnPropert === void 0 ? void 0 : _Object$getOwnPropert.get) != null;
        if (proxyHasGetter) {
          return Reflect.get(target, prop, receiver);
        }

        // Does this class implement the property
        var proxyHasProp = Object.prototype.hasOwnProperty.call(target, prop);

        // Does the class implement a function for the property
        var proxyHasFn = Object.prototype.hasOwnProperty.call(Object.getPrototypeOf(target), prop);
        var trueTarget = proxyHasProp || proxyHasFn ? target : target.model;
        return Reflect.get(trueTarget, prop);
      },
      set(target, prop, value) {
        var _Object$getOwnPropert2;
        var proxyHasSetter = ((_Object$getOwnPropert2 = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(target), prop)) === null || _Object$getOwnPropert2 === void 0 ? void 0 : _Object$getOwnPropert2.set) != null;
        var proxyHasProp = Object.prototype.hasOwnProperty.call(target, prop);
        if (proxyHasSetter || proxyHasProp) {
          return Reflect.set(target, prop, value, target);
        }
        return Reflect.set(target.model, prop, value, target.model);
      },
      // Mirror `get` so duck-type checks (`'prop' in model`) used by inner
      // model variants (e.g. pivot) resolve against the inner model when
      // the proxy itself doesn't define the property.
      has(target, prop) {
        if (Reflect.has(target, prop)) {
          return true;
        }
        return Reflect.has(target.model, prop);
      }
    });
  }
  close() {
    this.originalModel.close();
    if (this.model !== this.originalModel) {
      this.model.close();
    }
    if (this.modelPromise != null) {
      this.modelPromise.cancel();
    }
  }
  handleModelEvent(event) {
    log.debug2('handleModelEvent', event);
    var detail = event.detail,
      type = event.type;
    this.dispatchEvent(new EventShimCustomEvent(type, {
      detail
    }));
  }
  setModel(model) {
    log.debug('setModel', model);
    var oldModel = this.model;
    var oldColumns = oldModel.columns;
    if (oldModel !== this.originalModel) {
      oldModel.close();
    }
    this.model = model;
    if (this.listenerCount > 0) {
      this.addListeners(model);
    }
    if (oldModel !== model) {
      // Signal an inner-model swap so the host can reset view state owned by
      // the previous model (e.g. `movedColumns`). Dispatched before
      // COLUMNS_CHANGED / TABLE_CHANGED so the host resets moves first, then
      // reacts to the new column set.
      this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.SCHEMA_CHANGED, {
        detail: model
      }));
    }
    if (oldColumns !== model.columns) {
      this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.COLUMNS_CHANGED, {
        detail: model.columns
      }));
    } else if (this.currentViewport != null) {
      // If the columns haven't changed, the current viewport should still valid, and needs to be set on the new model
      var _this$currentViewport = this.currentViewport,
        top = _this$currentViewport.top,
        bottom = _this$currentViewport.bottom,
        columns = _this$currentViewport.columns;
      model.setViewport(top, bottom, columns);
    }
    if (isIrisGridTableModelTemplate(model)) {
      this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.TABLE_CHANGED, {
        detail: model.table
      }));
    }
  }
  setNextModel(modelPromise) {
    log.debug2('setNextModel');
    if (this.modelPromise) {
      this.modelPromise.cancel();
    }
    if (this.listenerCount > 0) {
      this.removeListeners(this.model);
    }
    this.modelPromise = PromiseUtils.makeCancelable(modelPromise, model => {
      // Don't close originalModel — it's the source-of-truth model owned
      // by this proxy, not a transient transform result. Closing it would
      // invalidate the underlying table for any consumer that later asks
      // to swap back to it.
      if (model !== this.originalModel) {
        model.close();
      }
    });
    this.modelPromise.then(model => {
      this.modelPromise = null;
      this.setModel(model);
    }).catch(err => {
      if (PromiseUtils.isCanceled(err)) {
        log.debug2('setNextModel cancelled');
        return;
      }
      log.error('Unable to set next model', err);
      this.modelPromise = null;
      this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.REQUEST_FAILED, {
        detail: err
      }));
    });
  }
  startListening() {
    super.startListening();
    this.addListeners(this.model);
  }
  stopListening() {
    super.stopListening();
    this.removeListeners(this.model);
  }
  addListeners(model) {
    var events = Object.keys(IrisGridModel.EVENT);
    for (var i = 0; i < events.length; i += 1) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      model.addEventListener(events[i], this.handleModelEvent);
    }
  }
  removeListeners(model) {
    var events = Object.keys(IrisGridModel.EVENT);
    for (var i = 0; i < events.length; i += 1) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      model.removeEventListener(events[i], this.handleModelEvent);
    }
  }
  get isCustomColumnsAvailable() {
    return this.model.isCustomColumnsAvailable &&
    // Disable for selectDistinct tables
    !(this.isSelectDistinctAvailable && this.selectDistinctColumns.length > 0);
  }
  get isRollupAvailable() {
    return (this.originalModel.isRollupAvailable || this.rollup != null) && this.selectDistinct.length === 0;
  }
  get isSelectDistinctAvailable() {
    return (
      // The presented model must support select distinct. Models that don't
      // (e.g. pivot models) report false here even though the underlying
      // source table would. This keeps the capability consistent with whatever
      // is currently displayed.
      this.model.isSelectDistinctAvailable && (this.originalModel.isSelectDistinctAvailable || this.selectDistinct.length > 0) && this.rollup == null
    );
  }
  get originalColumns() {
    return this.originalModel.columns;
  }
  get partitionColumns() {
    if (!isPartitionedGridModelProvider(this.originalModel)) {
      return [];
    }
    return this.originalModel.partitionColumns;
  }
  get partitionConfig() {
    if (!isPartitionedGridModelProvider(this.originalModel) || !this.originalModel.isPartitionRequired) {
      if (!isPartitionedGridModel(this.originalModel)) {
        // @ts-expect-error If the original model has an undefined partitionConfig return undefined to make the proxy model also return false in isPartitionedGridModel
        return undefined;
      }
      return null;
    }
    return this.partition;
  }
  set partitionConfig(partitionConfig) {
    if (!this.isPartitionRequired) {
      throw new Error('Partitions are not available');
    }
    // Skip if config hasn't changed to avoid unnecessary model swaps (e.g. on remount)
    if (this.partition === partitionConfig || this.partition != null && partitionConfig != null && this.partition.mode === partitionConfig.mode && this.partition.partitions.length === partitionConfig.partitions.length && this.partition.partitions.every((p, i) => p === partitionConfig.partitions[i])) {
      return;
    }
    log.debug('set partitionConfig', partitionConfig);
    this.partition = partitionConfig;
    var modelPromise = Promise.resolve(this.originalModel);
    if (partitionConfig != null && isPartitionedGridModelProvider(this.originalModel)) {
      if (partitionConfig.mode === 'keys') {
        modelPromise = this.originalModel.partitionBaseTable().then(table => makeModel(this.dh, table, this.formatter));
      } else if (partitionConfig.mode === 'merged') {
        modelPromise = this.originalModel.partitionMergedTable().then(table => makeModel(this.dh, table, this.formatter));
      } else {
        modelPromise = this.originalModel.partitionTable(partitionConfig.partitions).then(table => makeModel(this.dh, table, this.formatter));
      }
    }
    this.setNextModel(modelPromise);
  }
  partitionKeysTable() {
    if (!isPartitionedGridModelProvider(this.originalModel)) {
      throw new Error('Partitions are not available');
    }
    return this.originalModel.partitionKeysTable();
  }
  partitionMergedTable() {
    if (!isPartitionedGridModelProvider(this.originalModel)) {
      throw new Error('Partitions are not available');
    }
    return this.originalModel.partitionMergedTable();
  }
  partitionTable(partitions) {
    if (!isPartitionedGridModelProvider(this.originalModel)) {
      throw new Error('Partitions are not available');
    }
    return this.originalModel.partitionTable(partitions);
  }
  get rollupConfig() {
    return this.rollup;
  }
  set rollupConfig(rollupConfig) {
    log.debug('set rollupConfig', rollupConfig);
    if (!this.isRollupAvailable) {
      throw new Error('Rollup Rows are not available');
    }

    // Prevent model update when IrisGridModelUpdater is mounted
    // if rollup is already initialized in IrisGridPanel
    if (deepEqual(rollupConfig, this.rollup)) {
      return;
    }
    this.rollup = rollupConfig;
    var modelPromise = Promise.resolve(this.originalModel);
    if (isIrisGridTableModelTemplate(this.originalModel) && rollupConfig != null) {
      modelPromise = this.originalModel.table.rollup(rollupConfig).then(table => makeModel(this.dh, table, this.formatter));
    }
    this.setNextModel(modelPromise);
  }
  get selectDistinctColumns() {
    return this.selectDistinct;
  }
  set selectDistinctColumns(columnNames) {
    log.debug('set selectDistinctColumns', columnNames);
    if (!this.isSelectDistinctAvailable) {
      throw new Error('Select distinct is not available');
    }
    if (columnNames === this.selectDistinctColumns || columnNames.length === 0 && this.selectDistinctColumns.length === 0) {
      log.debug('Ignore same selectDistinctColumns', columnNames);
      return;
    }
    this.selectDistinct = columnNames;
    var selectDistinctColumns = columnNames.map(name => this.originalColumns.find(column => column.name === name)).filter(column => column != null);
    var modelPromise = Promise.resolve(this.originalModel);
    if (isIrisGridTableModelTemplate(this.originalModel) && selectDistinctColumns.length > 0) {
      modelPromise = this.originalModel.table.selectDistinct(selectDistinctColumns).then(table => makeModel(this.dh, table, this.formatter));
    }
    this.setNextModel(modelPromise);
  }
  set totalsConfig(totalsConfig) {
    if (this.modelPromise != null) {
      // Model switch in progress. Don't forward, as the config may reference stale columns.
      // COLUMNS_CHANGED will reapply the correct config after.
      return;
    }
    this.model.totalsConfig = totalsConfig;
  }
  get isFilterRequired() {
    return this.originalModel.isFilterRequired;
  }
  get isPartitionRequired() {
    // @ts-expect-error If the original model is not a partitioned model return undefined to make the proxy model also return false in isPartitionedGridModelProvider
    return isPartitionedGridModelProvider(this.originalModel) ? this.originalModel.isPartitionRequired : undefined;
  }
  get isPartitionAwareSourceTable() {
    return isPartitionedGridModelProvider(this.originalModel) && this.originalModel.isPartitionAwareSourceTable;
  }
  get formatter() {
    return this.originalModel.formatter;
  }
  set formatter(formatter) {
    this.originalModel.formatter = formatter;
  }
  get columnAlignmentMap() {
    return this.originalModel.columnAlignmentMap;
  }
  set columnAlignmentMap(columnAlignmentMap) {
    this.originalModel.columnAlignmentMap = columnAlignmentMap;
  }
}
export default IrisGridProxyModel;
//# sourceMappingURL=IrisGridProxyModel.js.map